import { eventAt, instantMs, type GuideEvent } from "@shared/epg-guide";
import type { TvLineupChannel } from "../../../hooks/useTvLineup";

/**
 * What a channel is showing, and — when it is showing nothing we know about —
 * WHY (`TV-4`, and the open half of `BE-22`).
 *
 * Every function here is pure and takes `now` as an argument. That is not
 * fastidiousness: "on now" is computed against the DEVICE clock because the
 * payload deliberately carries no `live` flag (the server read is cached in
 * ten-minute bins and a cached boolean is wrong within seconds — see
 * [[epg-guide]]), so the instant has to come from the caller's tick and never
 * from `Date.now()` buried in here where a test cannot reach it.
 */

/**
 * Why a channel has nothing to say about right now.
 *
 * ⚠️ **An unmapped channel is silent today.** Upstream answers `200 []` for a
 * channel that was never mapped to a guide source and for a channel whose
 * schedule simply ran out, so the two are indistinguishable in the events
 * alone — which is how a guest ends up looking at a blank row and concluding
 * the television is broken. `gaps[]` is what makes the emptiness measurable,
 * and this union is what makes it SAYABLE.
 */
export type ChannelGuideState =
  /** An event covers this instant. */
  | "live"
  /** The channel has a schedule, and a hole in it covers right now. */
  | "gap"
  /** The channel has no schedule at all — nothing is mapped. */
  | "unmapped";

export interface NowNext {
  state: ChannelGuideState;
  /** The programme on now, or null. */
  current: GuideEvent | null;
  /** What follows it — the next event to START after `now`, or null. */
  next: GuideEvent | null;
  /** The hole covering `now`, when there is one. */
  gap: { from: string; to: string; seconds: number } | null;
}

/**
 * What is on this channel at `now`, what is next, and what is missing.
 *
 * `eventAt` is the shared half-open answer (`start <= now < end`) that every
 * surface in the platform calls, so the CMS schedule panel and this screen can
 * never disagree about which of two adjacent programmes is on.
 */
export function nowNext(
  channel: Pick<TvLineupChannel, "events" | "gaps">,
  now: Date | number
): NowNext {
  const at = typeof now === "number" ? now : now.getTime();
  const current = eventAt(channel.events, at);
  // The next event is the first one that STARTS after this instant, which is
  // not always the array neighbour of `current`: a channel with a junction hole
  // has an event after the gap and nothing in between.
  const next =
    channel.events.find((e) => {
      const start = instantMs(e.startScheduled);
      return start !== null && start > at;
    }) ?? null;

  if (current) return { state: "live", current, next, gap: null };

  const gap =
    channel.gaps.find((g) => {
      const from = instantMs(g.from);
      const to = instantMs(g.to);
      return from !== null && to !== null && from <= at && at < to;
    }) ?? null;

  // No events ANYWHERE is the unmapped case. A channel whose guide merely ran
  // out still carries the events it did have, so it reads as a gap and says
  // "the guide stops here" rather than "this channel has no guide" — a
  // different sentence, and the honest one.
  const state: ChannelGuideState =
    channel.events.length === 0 ? "unmapped" : "gap";
  return { state, current: null, next, gap };
}

/** How far through the current programme we are, 0..1. */
export function progressOf(
  event: Pick<GuideEvent, "startScheduled" | "endScheduled">,
  now: Date | number
): number {
  const at = typeof now === "number" ? now : now.getTime();
  const start = instantMs(event.startScheduled);
  const end = instantMs(event.endScheduled);
  if (start === null || end === null || end <= start) return 0;
  return Math.max(0, Math.min(1, (at - start) / (end - start)));
}

/**
 * A programme's wall-clock span for this television — "19:00 – 19:45".
 *
 * Formatted from a `Date` in the device's own zone, deliberately: instants on
 * the wire are UTC with a `Z` ([[epg-guide]]'s convention) and a guest reads
 * the hour the hotel is in. This is the opposite of the Offers screen's rule,
 * where a slot is a wall clock the server already typed out — the difference is
 * that an EPG instant is a real moment and a booking slot is a label.
 */
export function eventClock(
  event: Pick<GuideEvent, "startScheduled" | "endScheduled">
): string {
  const from = clock(event.startScheduled);
  const to = clock(event.endScheduled);
  if (!from) return "";
  return to ? `${from} – ${to}` : from;
}

/** HH:mm in the device's zone, or "" for an instant that will not read. */
export function clock(instant: string): string {
  const ms = instantMs(instant);
  if (ms === null) return "";
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

/* -------------------------------------------------------------------------- */
/* Moving around the line-up                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The channel `delta` steps away, wrapping.
 *
 * Wraps because a channel list is a ring on every television a guest has ever
 * used: holding ch+ past the last channel returns to the first rather than
 * stopping dead, and a guest who cannot tell they have reached the end presses
 * the button four more times and decides the remote is broken.
 */
export function stepChannel(
  channels: readonly TvLineupChannel[],
  fromId: string | null,
  delta: number
): string | null {
  if (channels.length === 0) return null;
  const at = channels.findIndex((c) => c.id === fromId);
  const base = at < 0 ? 0 : at;
  const n = channels.length;
  const next = (((base + delta) % n) + n) % n;
  return channels[next].id;
}

export interface DigitResolution {
  /** The channel this buffer names, or null. */
  id: string | null;
  /**
   * True when a further digit could still name a DIFFERENT channel.
   *
   * This is what lets "5" tune immediately on a line-up numbered 1..9 while
   * still waiting on a line-up that also has a channel 51. A fixed timeout for
   * every entry makes single-digit zapping feel broken; no timeout at all makes
   * two-digit channels unreachable.
   */
  canExtend: boolean;
}

/**
 * What a typed run of digits means for this line-up.
 *
 * Leading zeroes are tolerated ("05" is channel 5) because a guest reaching for
 * a two-digit channel types the zero first on plenty of remotes.
 */
export function resolveDigits(
  channels: readonly TvLineupChannel[],
  buffer: string
): DigitResolution {
  if (!/^\d+$/.test(buffer)) return { id: null, canExtend: false };
  const typed = Number(buffer);
  const exact = channels.find((c) => c.number === typed);
  const canExtend = channels.some(
    (c) =>
      String(c.number).length > buffer.length &&
      String(c.number).startsWith(String(typed))
  );
  return { id: exact?.id ?? null, canExtend };
}

/** The widest channel number in the line-up, so entry stops at the right width. */
export function maxDigits(channels: readonly TvLineupChannel[]): number {
  return channels.reduce(
    (n, c) => Math.max(n, String(Math.max(0, c.number)).length),
    1
  );
}

/**
 * Add a digit to what the guest has typed, or refuse it.
 *
 * The width is capped so a fourth keypress cannot run the buffer away from a
 * line-up with two-digit numbers — but the cap counts SIGNIFICANT digits, not
 * characters. On a line-up numbered 1..9 the cap is one, and counting
 * characters would slice the `5` off `05` and leave the guest typing at a
 * channel that does not exist. Leading zeroes are how plenty of remotes reach a
 * two-digit channel, so tolerating them and then discarding the digit that
 * follows would be worse than not tolerating them at all.
 */
export function appendDigit(
  channels: readonly TvLineupChannel[],
  buffer: string,
  digit: string
): string {
  const next = buffer + digit;
  const significant = next.replace(/^0+/, "");
  return significant.length > maxDigits(channels) ? buffer : next;
}

/**
 * Can this channel actually be played on this television?
 *
 * A line-up may legitimately carry a channel this room is not entitled to or
 * that the catalogue has no media for. Saying so is the requirement; drawing a
 * black rectangle and waiting is what the vendor app does.
 */
export function tunable(channel: TvLineupChannel): boolean {
  return !!(channel.url || channel.contentAssetId);
}
